import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, apiPostJson, type PostValue } from './client'

export type InventoreeTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  title?: string
  external?: boolean
  anchor?: string
}

export type InventoreeShell = {
  pageTitle: string
  moduleUrl: string
  tabs: InventoreeTab[]
}

export const TABS = [
  'device_types',
  'subdevice_types',
  'device_categories',
  'brands',
  'type_form_builder',
  'softwares_licenses',
  'device_auto_decommissioned',
  'endpoint_status_settings',
  'performance_score',
  'alarms',
] as const

export type InventoreeTabId = (typeof TABS)[number]

export function isInventoreeTab(value: string): value is InventoreeTabId {
  return (TABS as readonly string[]).includes(value)
}

export function inventoreeShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'shell'],
    queryFn: () => apiGet<InventoreeShell>('/settings/inventoree'),
  })
}

export function brandsQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'brands'],
    queryFn: () => apiGet<{ brands: Array<Record<string, unknown>>; moduleUrl: string }>('/settings/inventoree/brands'),
  })
}

export function addBrand(brand_name: string) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/brands', { brand_name })
}

export function changeBrandStatus(brand_id: number, brand_status: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/brands/status', { brand_id, brand_status })
}

export function removeBrand(brand_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/brands/remove', { brand_id })
}

export function deviceTypesQuery(showDisabled = false) {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'device-types', showDisabled],
    queryFn: () =>
      apiGet<{ types: Array<Record<string, unknown>>; show_disabled: boolean; moduleUrl: string }>(
        '/settings/inventoree/device-types' + (showDisabled ? '?show_disabled=1' : ''),
      ),
  })
}

export function addDeviceType(type_name: string) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/device-types', { type_name })
}

export function changeTypeStatus(type_id: number, type_status: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/device-types/status', { type_id, type_status })
}

export function changeMonitoringStatus(type_id: number, type_status: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/device-types/monitoring', { type_id, type_status })
}

export function removeType(type_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/device-types/remove', { type_id })
}

export function typesToImportQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'import-types'],
    queryFn: () =>
      apiGet<{ importTypes: Array<Record<string, unknown>>; moduleUrl: string }>('/settings/inventoree/device-types/import'),
  })
}

export function saveImport(typeId: number[]) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/device-types/import', { typeId })
}

export function autoDecommissionQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'auto-decommission'],
    queryFn: () =>
      apiGet<{ types: Array<Record<string, unknown>>; moduleUrl: string }>('/settings/inventoree/auto-decommission'),
  })
}

export function changeAutoDecommissionStatus(type_id: number, type_status: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/auto-decommission/status', { type_id, type_status })
}

export function rebuildConfiguration() {
  return apiPost<{ result?: string }>('/settings/inventoree/auto-decommission/rebuild', {})
}

export function subdeviceTypesQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'subdevice-types'],
    queryFn: () =>
      apiGet<{
        Categories: Array<Record<string, unknown>>
        Currencies: Array<Record<string, unknown>>
        moduleUrl: string
      }>('/settings/inventoree/subdevice-types'),
  })
}

export function subtypeListQuery(showDisabled = false) {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'subtype-list', showDisabled],
    queryFn: () =>
      apiGet<{ list: Array<Record<string, unknown>>; show_disabled: boolean }>(
        '/settings/inventoree/subdevice-types/list' + (showDisabled ? '?show_disabled=1' : ''),
      ),
  })
}

export function saveSubtype(fields: Record<string, PostValue>) {
  return apiPost<{ status?: string; message?: string }>('/settings/inventoree/subdevice-types', fields)
}

export function changeSubTypeStatus(id: number) {
  return apiGet<{ status?: string; message?: string }>(`/settings/inventoree/subdevice-types/status?id=${id}`)
}

export function priceChange(fields: Record<string, PostValue>) {
  return apiPost<{ status?: string; message?: string }>('/settings/inventoree/subdevice-types/price', fields)
}

export function formBuilderQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'form-builder'],
    queryFn: () =>
      apiGet<{ device_types: Array<Record<string, unknown>>; moduleUrl: string }>('/settings/inventoree/form-builder'),
  })
}

export function deviceFormQuery(type: number) {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'form-builder', type],
    queryFn: () =>
      apiGet<{
        empty?: string
        fields?: Array<Record<string, unknown>>
        moduleUrl?: string
      }>(`/settings/inventoree/form-builder/${type}`),
    enabled: type > 0,
  })
}

export function saveBuildForm(type: number, payload: unknown) {
  return apiPostJson<{ ok?: boolean }>(`/settings/inventoree/form-builder/${type}`, payload)
}

export function licensesQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'licenses'],
    queryFn: () =>
      apiGet<{
        softwares: Array<Record<string, unknown>>
        company_id: number
        moduleUrl: string
      }>('/settings/inventoree/licenses'),
  })
}

export function licensesTableQuery(selectedLicense: number) {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'licenses-table', selectedLicense],
    queryFn: () =>
      apiGet<{
        draw: number
        recordsTotal: number
        recordsFiltered: number
        data: Array<Record<string, unknown>>
      }>(`/settings/inventoree/licenses/table?selectedLicense=${selectedLicense}&start=0&length=200`),
  })
}

export function newLicense(form: FormData) {
  return apiPostForm<{ licenseId?: number }>('/settings/inventoree/licenses', form)
}

export function removeLicense(license_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/licenses/remove', { license_id })
}

export function updateLicense(form: FormData) {
  return apiPostForm<{ ok?: boolean }>('/settings/inventoree/licenses/update', form)
}

export function assignDevicesLicense(license: number, license_devices: number[]) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/licenses/assign', { license, license_devices })
}

export function removeDeviceLicense(license_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/licenses/device/remove', { license_id })
}

export function categoriesQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'categories'],
    queryFn: () =>
      apiGet<{
        categories: Array<Record<string, unknown>>
        deviceTypes: Array<Record<string, unknown>>
        moduleUrl: string
      }>('/settings/inventoree/categories'),
  })
}

export function addDeviceCategory(category_name: string, device_types: number[]) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/categories', { category_name, device_types })
}

export function editDeviceCategory(category: number, category_name: string, device_types: number[]) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/categories/edit', { category, category_name, device_types })
}

export function changeCategoryStatus(category_id: number, category_status: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/categories/status', { category_id, category_status })
}

export function removeCategory(category_id: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/categories/remove', { category_id })
}

export function categoryDetails(category: number) {
  return apiPost<{
    categoryDetails: Record<string, unknown>
    categoryTypes: Array<Record<string, unknown>>
    deviceTypes: Array<Record<string, unknown>>
  }>('/settings/inventoree/categories/details', { category })
}

export function performanceScoreQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'score'],
    queryFn: () =>
      apiGet<{
        settings: Record<string, number>
        available: boolean
        defaults: Record<string, number>
        distribution: Record<string, number>
        bands: Record<string, { name: string; label: string }>
      }>('/settings/inventoree/performance-score'),
  })
}

export function savePerformanceScore(fields: Record<string, PostValue>) {
  return apiPost<{
    saved: boolean
    settings: Record<string, number>
    distribution: Record<string, number>
  }>('/settings/inventoree/performance-score', fields)
}

export function alarmsQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'alarms'],
    queryFn: () =>
      apiGet<{ categories: Array<Record<string, unknown> & { alert_types?: Array<Record<string, unknown>> }> }>(
        '/settings/inventoree/alarms',
      ),
  })
}

export function getAlarms(type: number) {
  return apiGet<{
    default: boolean
    alertType: Record<string, unknown> | null
    alarms: Array<Record<string, unknown>>
  }>(`/settings/inventoree/alarms/${type}`)
}

export function editAlarmForm(id: number) {
  return apiGet<{
    alarm: Record<string, unknown>
    alarmType: Record<string, unknown>
    notifiers: Array<Record<string, unknown>>
  }>(`/settings/inventoree/alarms/${id}/edit`)
}

export function createAlarmForm(type: number) {
  return apiGet<{ alarm: Record<string, unknown> }>(`/settings/inventoree/alarms/create-form/${type}`)
}

export function updateAlarm(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/alarms/update', fields)
}

export function createAlarm(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/alarms', fields)
}

export function loadDefaultSettings(type: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/alarms/defaults', { type })
}

export function changeAlarmStatus(alarm: number, activate: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/alarms/status', { alarm, activate })
}

export function removeAlarm(alarm: number) {
  return apiPost<{ ok?: boolean }>('/settings/inventoree/alarms/remove', { alarm })
}

export function checkAlarmValue(fields: Record<string, PostValue>) {
  return apiPost<{ valid: string }>('/settings/inventoree/alarms/check-value', fields)
}

export function endpointQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'endpoint'],
    queryFn: () =>
      apiGet<{
        /*
         * Capatul da azi forma intreaga, cu zerouri. Tipul pastreaza `null`
         * fiindca asa a raspuns pana azi pentru o firma fara nicio salvare, si
         * ecranul trebuie sa tina si in fata unui raspuns vechi - din cache, sau
         * de la un server care n-a primit inca livrarea.
         */
        settings: Record<string, Record<string, unknown> | undefined> | null
        connections: Record<string, unknown> | null
        company: number
      }>('/settings/inventoree/endpoint'),
  })
}

export function endpointIpsQuery() {
  return queryOptions({
    queryKey: ['settings', 'inventoree', 'endpoint-ips'],
    queryFn: () => apiGet<{ ips: Array<Record<string, unknown>> | number }>('/settings/inventoree/endpoint/ips'),
  })
}

export function updateEndpoint(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean; status?: string; message?: string }>('/settings/inventoree/endpoint', fields)
}

export function addIp(fields: Record<string, PostValue>) {
  return apiPost<{ status?: string; message?: string }>('/settings/inventoree/endpoint/ips/add', fields)
}

export function removeIp(id: number) {
  return apiPost<{ status?: string }>('/settings/inventoree/endpoint/ips/remove', { id })
}
